import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocalStorage } from '../useLocalStorage';
import VirtualKeyboard from '../keyboard/VirtualKeyboard';
import styles from './WordleStep.module.css';

type LetterState = 'correct' | 'present' | 'absent' | 'empty';

export interface WordleStepProps {
  answer: string; // must be 5 letters
  maxGuesses?: number;
  onSolved?: () => void;
  title?: string;
  disabled?: boolean; // if true, input is disabled (locked)
  initiallySolved?: boolean; // if true, show as solved on mount
  solvedMessage?: string; // optional message to show when solved
  storageKey?: string; // persist guesses/status
}

function normalizeWord(word: string): string {
  return word.trim().toLowerCase();
}

function evaluateGuess(guessInput: string, answerInput: string): LetterState[] {
  const guess = normalizeWord(guessInput);
  const answer = normalizeWord(answerInput);

  const result: LetterState[] = new Array(5).fill('absent');
  const answerCharCounts: Record<string, number> = {};

  for (let i = 0; i < 5; i += 1) {
    const ch = answer[i];
    answerCharCounts[ch] = (answerCharCounts[ch] ?? 0) + 1;
  }

  // First pass: mark correct
  for (let i = 0; i < 5; i += 1) {
    if (guess[i] === answer[i]) {
      result[i] = 'correct';
      answerCharCounts[guess[i]] -= 1;
    }
  }

  // Second pass: mark present
  for (let i = 0; i < 5; i += 1) {
    if (result[i] === 'correct') continue;
    const ch = guess[i];
    if (answerCharCounts[ch] > 0) {
      result[i] = 'present';
      answerCharCounts[ch] -= 1;
    } else {
      result[i] = 'absent';
    }
  }

  return result;
}

export const WordleStep: React.FC<WordleStepProps> = ({
  answer,
  maxGuesses = 6,
  onSolved,
  title = 'Step: Guess the 5-letter word',
  disabled = false,
  initiallySolved = false,
  solvedMessage,
  storageKey,
}) => {
  const canonicalAnswer = useMemo(() => normalizeWord(answer).slice(0, 5), [answer]);
  type Persisted = { guesses: string[]; status: 'playing' | 'won' | 'lost'; answer: string };
  const [persisted, setPersisted] = useLocalStorage<Persisted>(
    storageKey ?? `wordle:${canonicalAnswer}`,
    { guesses: [], status: initiallySolved ? 'won' : 'playing', answer: canonicalAnswer }
  );
  const guesses = persisted.guesses;
  const status = persisted.status;
  const [currentGuess, setCurrentGuess] = useState<string>('');
  const isDisabled = status !== 'playing' || disabled;

  // If parent indicates this step is solved, ensure local status is 'won'
  useEffect(() => {
    if (initiallySolved && status !== 'won') {
      setPersisted((prev) => ({ ...prev, status: 'won', answer: canonicalAnswer }));
    }
  }, [initiallySolved, status, setPersisted, canonicalAnswer]);

  // If the stored answer does not match current answer, reset persisted state
  useEffect(() => {
    if (persisted.answer !== canonicalAnswer) {
      setPersisted({ guesses: [], status: initiallySolved ? 'won' : 'playing', answer: canonicalAnswer });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canonicalAnswer, storageKey]);

  const rows = useMemo(() => {
    const filledRows = guesses.map((g) => ({
      letters: g.padEnd(5, ' ').slice(0, 5).split(''),
      states: evaluateGuess(g, canonicalAnswer),
    }));
    const currentRowCount = Math.max(0, maxGuesses - filledRows.length - (status === 'playing' ? 1 : 0));
    const emptyRows = new Array(currentRowCount).fill(null).map(() => ({
      letters: new Array(5).fill(' '),
      states: new Array<LetterState>(5).fill('empty'),
    }));

    const currentRow = status === 'playing'
      ? [{
          letters: currentGuess.padEnd(5, ' ').slice(0, 5).split(''),
          states: new Array<LetterState>(5).fill('empty'),
        }]
      : [];

    return [...filledRows, ...currentRow, ...emptyRows].slice(0, maxGuesses);
  }, [guesses, currentGuess, canonicalAnswer, maxGuesses, status]);

  // Derive per-letter status across all submitted guesses for keyboard coloring
  const letterStatusMap = useMemo(() => {
    const map: Record<string, LetterState> = {};
    function rank(state: LetterState) {
      return state === 'correct' ? 3 : state === 'present' ? 2 : state === 'absent' ? 1 : 0;
    }
    for (const g of guesses) {
      const states = evaluateGuess(g, canonicalAnswer);
      for (let i = 0; i < 5; i += 1) {
        const ch = g[i]?.toLowerCase();
        if (!ch || ch < 'a' || ch > 'z') continue;
        const s = states[i];
        if (!map[ch] || rank(s) > rank(map[ch])) {
          map[ch] = s;
        }
      }
    }
    return map;
  }, [guesses, canonicalAnswer]);

  const submitGuess = useCallback(() => {
    const guess = normalizeWord(currentGuess);
    if (guess.length !== 5 || isDisabled) return;

    const nextGuesses = [...guesses, guess];
    setCurrentGuess('');

    if (guess === canonicalAnswer) {
      setPersisted({ guesses: nextGuesses, status: 'won', answer: canonicalAnswer });
      if (onSolved) onSolved();
      return;
    }

    if (nextGuesses.length >= maxGuesses) {
      setPersisted({ guesses: nextGuesses, status: 'lost', answer: canonicalAnswer });
    } else {
      setPersisted({ guesses: nextGuesses, status: 'playing', answer: canonicalAnswer });
    }
  }, [currentGuess, isDisabled, guesses, canonicalAnswer, maxGuesses, onSolved, setPersisted]);

  function handleKeyInput(key: string) {
    if (isDisabled) return;
    if (key === 'ENTER') {
      submitGuess();
      return;
    }
    if (key === 'DEL') {
      setCurrentGuess((v) => v.slice(0, -1));
      return;
    }
    const letter = key.toLowerCase();
    if (!/^[a-z]$/.test(letter)) return;
    setCurrentGuess((v) => (v.length < 5 ? (v + letter).slice(0, 5) : v));
  }

  // Support physical keyboard input for accessibility
  useEffect(() => {
    if (disabled) return;
    function onKeyDown(e: KeyboardEvent) {
      if (isDisabled) return;
      const key = e.key;
      if (key === 'Enter') {
        e.preventDefault();
        submitGuess();
        return;
      }
      if (key === 'Backspace' || key === 'Delete') {
        e.preventDefault();
        setCurrentGuess((v) => v.slice(0, -1));
        return;
      }
      if (/^[a-zA-Z]$/.test(key)) {
        e.preventDefault();
        setCurrentGuess((v) => (v.length < 5 ? (v + key.toLowerCase()).slice(0, 5) : v));
      }
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [disabled, submitGuess, isDisabled]);


  return (
    <div className={styles.container}>
      {title && <h2 className={styles.title}>{title}</h2>}
      {disabled && status === 'playing' && (
        <div className="locked-banner" aria-hidden>
          Locked. Solve the previous step to unlock this one.
        </div>
      )}
      <div className={styles.grid} role="grid" aria-label="wordle grid">
        {rows.map((row, rowIdx) => (
          <div className={styles.row} role="row" key={`row-${rowIdx}`}>
            {row.letters.map((ch, colIdx) => (
              <div
                key={`cell-${rowIdx}-${colIdx}`}
                className={[
                  styles.cell,
                  row.states[colIdx] === 'correct'
                    ? styles.correct
                    : row.states[colIdx] === 'present'
                    ? styles.present
                    : row.states[colIdx] === 'absent'
                    ? styles.absent
                    : ''
                ].join(' ')}
              >
                {ch.toUpperCase()}
              </div>
            ))}
          </div>
        ))}
      </div>

            <VirtualKeyboard
        onKeyPress={handleKeyInput}
        disabled={isDisabled}
        letterStatusMap={letterStatusMap}
        enterDisabled={currentGuess.length !== 5}
        deleteDisabled={currentGuess.length === 0}
      />

      {status === 'lost' && (
        <div className={[styles.result, styles.failure].join(' ')} role="status">Out of guesses. The word was “{canonicalAnswer.toUpperCase()}”.</div>
      )}
    </div>
  );
};

export default WordleStep;


